import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchAcademyPage,
  fetchAcademyLatestCourses,
  fetchAcademyMainCategories,
} from "@/api/academies";
import {
  FaStar,
  FaChevronRight,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBook,
  FaQuestionCircle,
  FaQuoteRight,
  FaPaperPlane,
  FaCheck,
  FaBars,
  FaTimes,
  FaSearch,
  FaPlay,
  FaClock,
  FaUsers,
  FaCertificate,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaYoutube,
  FaArrowRight,
  FaFire,
  FaRocket,
  FaTrophy,
  FaShieldAlt,
  FaHeadset,
  FaGlobe,
  FaChartLine,
  FaGraduationCap,
  FaLaptopCode,
  FaPaintBrush,
  FaBullhorn,
  FaBriefcase,
  FaRegHeart,
  FaShare,
  FaEye,
  FaLanguage,
  FaCrown,
  FaGem,
  FaMedal,
  FaMagic, // استخدم FaMagic بدلاً من FaMagic 
  FaPalette,
  FaWaveSquare,
  // إزالة FaMagic 
} from "react-icons/fa";


export default function AcademyPage() {
  const { academyId } = useParams();
  const navigate = useNavigate();

  const [academy, setAcademy] = useState(null);
  const [plans, setPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [contactLinks, setContactLinks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [latestCourses, setLatestCourses] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFaq, setActiveFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const academyData = await fetchAcademyPage(academyId);
        setAcademy(academyData.academy);
        setPlans(academyData.plans || []);
        setFaqs(academyData.faqs || []);
        setContactLinks(academyData.contact_links || []);
        setTestimonials(academyData.testimonials || []);
        setTrainers(academyData.trainers || []);

        const coursesData = await fetchAcademyLatestCourses(academyId);
        setLatestCourses(coursesData || []);

        const categoriesData = await fetchAcademyMainCategories(academyId);
        setMainCategories(categoriesData || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [academyId]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  // إضافة CSS داخل الملف
  const styles = `
    /* ============== CSS Variables ============== */
    :root {
      --primary: #6a11cb;
      --primary-light: #8a2be2;
      --primary-dark: #4a00e0;
      --secondary: #00dbde;
      --secondary-light: #00c6ff;
      --accent: #ff6b6b;
      --accent-light: #ff9e7d;
      --accent-dark: #ff4757;
      --dark: #1a1a2e;
      --dark-light: #16213e;
      --light: #f8f9fa;
      --light-gray: #e9ecef;
      --gray: #6c757d;
      --success: #00b894;
      --warning: #fdcb6e;
      --danger: #ff7675;
      --info: #00cec9;
      
      --gradient-primary: linear-gradient(135deg, var(--primary), var(--primary-light), var(--secondary));
      --gradient-secondary: linear-gradient(135deg, var(--secondary), var(--secondary-light), #36d1dc);
      --gradient-accent: linear-gradient(135deg, var(--accent), var(--accent-light), #ff9a9e);
      --gradient-dark: linear-gradient(135deg, var(--dark), var(--dark-light), #0f3460);
      
      --border-radius-lg: 20px;
      --border-radius-md: 15px;
      --border-radius-sm: 10px;
      
      --box-shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      --box-shadow-md: 0 20px 40px rgba(0, 0, 0, 0.1);
      --box-shadow-sm: 0 10px 30px rgba(0, 0, 0, 0.08);
      --box-shadow-hover: 0 35px 60px rgba(106, 17, 203, 0.15);
      
      --transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      --transition-fast: all 0.3s ease;
      
      --glass-bg: rgba(255, 255, 255, 0.1);
      --glass-border: rgba(255, 255, 255, 0.2);
      --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      
      --neon-primary: 0 0 20px rgba(106, 17, 203, 0.5);
      --neon-secondary: 0 0 20px rgba(0, 219, 222, 0.5);
      --neon-accent: 0 0 20px rgba(255, 107, 107, 0.5);
    }

    /* ============== Reset & Base ============== */
    .academy-landing {
      font-family: 'Poppins', 'Tajawal', 'Segoe UI', sans-serif;
      line-height: 1.7;
      color: var(--dark);
      background: var(--light);
      direction: rtl;
      overflow-x: hidden;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html {
      scroll-behavior: smooth;
    }

    ::-webkit-scrollbar {
      width: 10px;
    }

    ::-webkit-scrollbar-track {
      background: linear-gradient(var(--dark), var(--dark-light));
    }

    ::-webkit-scrollbar-thumb {
      background: var(--gradient-primary);
      border-radius: 5px;
    }

    ::selection {
      background: var(--primary);
      color: white;
    }

    .container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 0 30px;
    }

    section {
      padding: 120px 0;
      position: relative;
    }

    /* ============== Animations ============== */
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-20px); }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }

    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }

    @keyframes slideInUp {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    @keyframes neonGlow {
      0%, 100% { 
        box-shadow: var(--neon-primary);
        filter: brightness(1);
      }
      50% { 
        box-shadow: var(--neon-secondary);
        filter: brightness(1.2);
      }
    }

    /* ============== Loading & Error ============== */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: var(--gradient-dark);
      position: relative;
      overflow: hidden;
    }

    .loading-container::before {
      content: '';
      position: absolute;
      width: 300%;
      height: 300%;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(106, 17, 203, 0.1) 50%,
        transparent 70%
      );
      animation: rotate 10s linear infinite;
    }

    .spinner {
      width: 80px;
      height: 80px;
      border: 4px solid transparent;
      border-radius: 50%;
      border-top: 4px solid var(--secondary);
      border-right: 4px solid var(--primary);
      border-bottom: 4px solid var(--accent);
      animation: rotate 1s linear infinite;
      margin-bottom: 30px;
      position: relative;
      z-index: 2;
    }

    .spinner::before {
      content: '';
      position: absolute;
      top: -4px;
      right: -4px;
      bottom: -4px;
      left: -4px;
      border-radius: 50%;
      border: 2px solid transparent;
      border-top: 2px solid rgba(255, 255, 255, 0.3);
      animation: rotate 2s linear infinite reverse;
    }

    .loading-container h2 {
      color: white;
      font-size: 2rem;
      font-weight: 600;
      position: relative;
      z-index: 2;
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: pulse 2s ease-in-out infinite;
    }

    .error-container {
      text-align: center;
      padding: 100px 20px;
      background: var(--gradient-accent);
      color: white;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
    }

    .error-container::before {
      content: '';
      position: absolute;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
      background-size: 50px 50px;
      animation: rotate 20s linear infinite;
    }

    .error-container h2 {
      font-size: 3.5rem;
      margin-bottom: 30px;
      position: relative;
      z-index: 2;
      text-shadow: 3px 3px 0 rgba(0,0,0,0.1);
    }

    /* ============== Header ============== */
    .header {
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      z-index: 1000;
      padding: 20px 0;
      transition: var(--transition);
      backdrop-filter: blur(10px);
      background: rgba(255, 255, 255, 0.95);
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }

    .header.scrolled {
      padding: 15px 0;
      background: rgba(26, 26, 46, 0.95);
      box-shadow: var(--box-shadow-md);
      border-bottom: 1px solid rgba(106, 17, 203, 0.2);
    }

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 15px;
      text-decoration: none;
      color: var(--dark);
    }

    .header.scrolled .logo {
      color: white;
    }

    .logo-icon {
      width: 50px;
      height: 50px;
      background: var(--gradient-primary);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 24px;
      box-shadow: var(--neon-primary);
      animation: neonGlow 3s ease-in-out infinite;
    }

    .logo-text {
      font-size: 1.8rem;
      font-weight: 800;
      background: linear-gradient(45deg, var(--primary), var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .header.scrolled .logo-text {
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .nav-links {
      display: flex;
      gap: 40px;
      list-style: none;
    }

    .nav-link {
      color: var(--dark);
      text-decoration: none;
      font-weight: 600;
      font-size: 1.1rem;
      position: relative;
      padding: 8px 0;
      transition: var(--transition);
      background: linear-gradient(45deg, var(--primary), var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-size: 200% 100%;
      background-position: 100%;
    }

    .header.scrolled .nav-link {
      color: white;
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .nav-link::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 0;
      height: 3px;
      background: var(--gradient-primary);
      transition: var(--transition);
      border-radius: 2px;
    }

    .nav-link:hover {
      background-position: 0%;
    }

    .nav-link:hover::before {
      width: 100%;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 20px;
    }

    .search-btn {
      background: none;
      border: none;
      color: var(--dark);
      font-size: 1.2rem;
      cursor: pointer;
      transition: var(--transition);
      padding: 10px;
      border-radius: 50%;
      background: var(--light-gray);
    }

    .header.scrolled .search-btn {
      color: white;
      background: rgba(255, 255, 255, 0.1);
    }

    .search-btn:hover {
      transform: scale(1.1);
      background: var(--gradient-primary);
      color: white;
    }

    .mobile-menu-btn {
      display: none;
      background: none;
      border: none;
      color: var(--dark);
      font-size: 1.5rem;
      cursor: pointer;
      padding: 10px;
      z-index: 1001;
    }

    .header.scrolled .mobile-menu-btn {
      color: white;
    }

    /* ============== Mobile Menu ============== */
    .mobile-menu {
      position: fixed;
      top: 0;
      right: -100%;
      width: 300px;
      height: 100vh;
      background: rgba(26, 26, 46, 0.98);
      backdrop-filter: blur(20px);
      z-index: 1000;
      transition: var(--transition);
      padding: 100px 30px 30px;
      display: flex;
      flex-direction: column;
      gap: 30px;
    }

    .mobile-menu.open {
      right: 0;
      box-shadow: -20px 0 40px rgba(0, 0, 0, 0.3);
    }

    .mobile-nav-links {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 25px;
    }

    .mobile-nav-link {
      color: white;
      text-decoration: none;
      font-size: 1.3rem;
      font-weight: 600;
      padding: 15px 20px;
      border-radius: var(--border-radius-md);
      transition: var(--transition);
      background: rgba(255, 255, 255, 0.05);
      display: flex;
      align-items: center;
      gap: 15px;
    }

    .mobile-nav-link:hover {
      background: var(--gradient-primary);
      transform: translateX(-10px);
    }

    .mobile-nav-link svg {
      font-size: 1.2rem;
    }

    .mobile-menu-actions {
      margin-top: auto;
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    /* ============== Buttons ============== */
    .btn {
      padding: 16px 36px;
      border-radius: var(--border-radius-md);
      font-size: 1.1rem;
      font-weight: 700;
      cursor: pointer;
      transition: var(--transition);
      border: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      text-decoration: none;
      position: relative;
      overflow: hidden;
      z-index: 1;
    }

    .btn::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: inherit;
      z-index: -1;
      transition: var(--transition);
    }

    .btn:hover::before {
      transform: scale(1.1);
    }

    .btn-primary {
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--neon-primary);
      animation: neonGlow 3s ease-in-out infinite;
    }

    .btn-primary:hover {
      transform: translateY(-5px) scale(1.05);
      box-shadow: var(--box-shadow-hover);
    }

    .btn-secondary {
      background: var(--gradient-secondary);
      color: white;
      box-shadow: 0 10px 30px rgba(0, 219, 222, 0.3);
    }

    .btn-secondary:hover {
      transform: translateY(-5px);
      box-shadow: 0 20px 40px rgba(0, 219, 222, 0.4);
    }

    .btn-accent {
      background: var(--gradient-accent);
      color: white;
      box-shadow: 0 10px 30px rgba(255, 107, 107, 0.3);
    }

    .btn-accent:hover {
      transform: translateY(-5px);
      box-shadow: 0 20px 40px rgba(255, 107, 107, 0.4);
    }

    .btn-outline {
      background: transparent;
      color: var(--primary);
      border: 2px solid var(--primary);
      position: relative;
      overflow: hidden;
    }

    .btn-outline::before {
      content: '';
      position: absolute;
      top: 50%;
      right: 50%;
      width: 0;
      height: 0;
      background: var(--gradient-primary);
      border-radius: 50%;
      transform: translate(50%, -50%);
      transition: width 0.6s, height 0.6s;
      z-index: -1;
    }

    .btn-outline:hover {
      color: white;
      border-color: transparent;
    }

    .btn-outline:hover::before {
      width: 300%;
      height: 300%;
    }

    .btn-glass {
      background: var(--glass-bg);
      backdrop-filter: blur(10px);
      border: 1px solid var(--glass-border);
      color: white;
      box-shadow: var(--glass-shadow);
    }

    .btn-glass:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-5px);
    }

    .btn-icon {
      width: 50px;
      height: 50px;
      padding: 0;
      border-radius: 50%;
      font-size: 1.2rem;
    }

    .btn-social {
      width: 45px;
      height: 45px;
      padding: 0;
      border-radius: 50%;
      font-size: 1rem;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-social:hover {
      background: var(--gradient-primary);
      transform: translateY(-3px) rotate(5deg);
      border-color: transparent;
    }

    /* ============== Hero Section ============== */
    .hero {
      min-height: 100vh;
      display: flex;
      align-items: center;
      background: linear-gradient(135deg, var(--dark) 0%, var(--dark-light) 50%, var(--primary-dark) 100%);
      position: relative;
      overflow: hidden;
      padding-top: 100px;
    }

    .hero::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: 
        radial-gradient(circle at 20% 80%, rgba(106, 17, 203, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(0, 219, 222, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(255, 107, 107, 0.2) 0%, transparent 50%);
      animation: gradient 15s ease infinite;
      background-size: 400% 400%;
    }

    .hero::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E");
      opacity: 0.3;
    }

    .hero-content {
      position: relative;
      z-index: 2;
      max-width: 800px;
      animation: slideInUp 1s ease-out;
    }

    .hero-title {
      font-size: 4.5rem;
      font-weight: 900;
      margin-bottom: 25px;
      line-height: 1.1;
      background: linear-gradient(45deg, white, var(--secondary), var(--primary-light));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
      position: relative;
      display: inline-block;
    }

    .hero-title::after {
      content: '';
      position: absolute;
      bottom: -15px;
      right: 0;
      width: 200px;
      height: 5px;
      background: var(--gradient-primary);
      border-radius: 5px;
      box-shadow: var(--neon-primary);
    }

    .hero-subtitle {
      font-size: 1.4rem;
      margin-bottom: 40px;
      color: rgba(255, 255, 255, 0.9);
      line-height: 1.8;
      max-width: 600px;
    }

    .hero-buttons {
      display: flex;
      gap: 25px;
      flex-wrap: wrap;
      margin-bottom: 60px;
    }

    .hero-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 30px;
      background: var(--glass-bg);
      backdrop-filter: blur(20px);
      border: 1px solid var(--glass-border);
      padding: 40px;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--glass-shadow);
    }

    .stat-item {
      text-align: center;
      position: relative;
      padding: 20px;
      border-radius: var(--border-radius-md);
      background: rgba(255, 255, 255, 0.05);
      transition: var(--transition);
    }

    .stat-item:hover {
      transform: translateY(-10px);
      background: rgba(255, 255, 255, 0.1);
      box-shadow: var(--neon-primary);
    }

    .stat-icon {
      font-size: 2.5rem;
      margin-bottom: 15px;
      color: var(--secondary);
      filter: drop-shadow(0 0 10px rgba(0, 219, 222, 0.5));
    }

    .stat-number {
      font-size: 3rem;
      font-weight: 800;
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 10px;
      line-height: 1;
    }

    .stat-label {
      color: rgba(255, 255, 255, 0.8);
      font-size: 1rem;
      font-weight: 600;
    }

    /* ============== Floating Elements ============== */
    .floating-elements {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      right: 0;
      pointer-events: none;
    }

    .floating-element {
      position: absolute;
      width: 60px;
      height: 60px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.5rem;
      box-shadow: var(--neon-primary);
      animation: float 6s ease-in-out infinite;
    }

    .floating-element:nth-child(1) {
      top: 20%;
      right: 10%;
      animation-delay: 0s;
      background: var(--gradient-primary);
    }

    .floating-element:nth-child(2) {
      top: 60%;
      right: 20%;
      animation-delay: 2s;
      background: var(--gradient-secondary);
      width: 80px;
      height: 80px;
    }

    .floating-element:nth-child(3) {
      top: 40%;
      left: 15%;
      animation-delay: 4s;
      background: var(--gradient-accent);
    }

    .floating-element:nth-child(4) {
      top: 80%;
      left: 10%;
      animation-delay: 1s;
      background: var(--gradient-primary);
      width: 40px;
      height: 40px;
    }

    /* ============== Section Headers ============== */
    .section-header {
      text-align: center;
      margin-bottom: 80px;
      position: relative;
    }

    .section-badge {
      display: inline-block;
      background: var(--gradient-primary);
      color: white;
      padding: 10px 25px;
      border-radius: 30px;
      font-size: 0.9rem;
      font-weight: 700;
      letter-spacing: 1px;
      margin-bottom: 25px;
      box-shadow: var(--neon-primary);
      animation: neonGlow 3s ease-in-out infinite;
    }

    .section-title {
      font-size: 3.5rem;
      font-weight: 900;
      margin-bottom: 25px;
      background: linear-gradient(45deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      position: relative;
      display: inline-block;
    }

    .section-title::after {
      content: '';
      position: absolute;
      bottom: -15px;
      right: 50%;
      transform: translateX(50%);
      width: 100px;
      height: 5px;
      background: var(--gradient-primary);
      border-radius: 5px;
      box-shadow: var(--neon-primary);
    }

    .section-subtitle {
      font-size: 1.3rem;
      color: var(--gray);
      max-width: 700px;
      margin: 30px auto 0;
      line-height: 1.8;
    }

    /* ============== Features Section ============== */
    .features {
      background: white;
      position: relative;
      overflow: hidden;
    }

    .features::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle at 30% 70%, rgba(106, 17, 203, 0.05) 0%, transparent 50%);
    }

    .features-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
      gap: 40px;
    }

    .feature-card {
      background: white;
      padding: 50px 40px;
      border-radius: var(--border-radius-lg);
      position: relative;
      overflow: hidden;
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition);
      border: 2px solid transparent;
      z-index: 1;
    }

    .feature-card::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: var(--gradient-primary);
      opacity: 0;
      transition: var(--transition);
      z-index: -1;
    }

    .feature-card:hover {
      transform: translateY(-20px);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover);
    }

    .feature-card:hover::before {
      opacity: 0.05;
    }

    .feature-card:hover .feature-icon {
      transform: scale(1.1) rotate(10deg);
      background: var(--gradient-primary);
    }

    .feature-icon {
      width: 100px;
      height: 100px;
      background: var(--light-gray);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 30px;
      font-size: 40px;
      color: var(--primary);
      transition: var(--transition);
      position: relative;
    }

    .feature-icon::after {
      content: '';
      position: absolute;
      top: -10px;
      right: -10px;
      bottom: -10px;
      left: -10px;
      border: 2px solid var(--primary);
      border-radius: 50%;
      opacity: 0.2;
      animation: rotate 20s linear infinite;
    }

    .feature-title {
      font-size: 1.8rem;
      font-weight: 800;
      margin-bottom: 20px;
      color: var(--dark);
      text-align: center;
    }

    .feature-description {
      color: var(--gray);
      font-size: 1.1rem;
      line-height: 1.8;
      text-align: center;
    }

    /* ============== Categories Section ============== */
    .categories {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      position: relative;
    }

    .categories::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M45 90a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-25a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm25 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm25 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm25 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm25 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0 25a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm-25 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm-25 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm-25 0a5 5 0 1 1 0-10 5 5 0 0 1 0 10z' fill='%236a11cb' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E");
      opacity: 0.5;
    }

    .categories-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
      gap: 40px;
      position: relative;
      z-index: 2;
    }

    .category-card {
      background: white;
      border-radius: var(--border-radius-lg);
      overflow: hidden;
      box-shadow: var(--box-shadow-md);
      transition: var(--transition);
      cursor: pointer;
      border: 2px solid transparent;
      position: relative;
    }

    .category-card:hover {
      transform: translateY(-20px) scale(1.02);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover);
    }

    .category-card:hover .category-image {
      transform: scale(1.1);
    }

    .category-image {
      height: 250px;
      background: linear-gradient(45deg, var(--primary), var(--secondary));
      position: relative;
      overflow: hidden;
      transition: transform 0.8s ease;
    }

    .category-image::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(45deg, rgba(255,255,255,0.2) 0%, transparent 100%);
    }

    .category-content {
      padding: 40px 35px;
      position: relative;
    }

    .category-content::before {
      content: '';
      position: absolute;
      top: -20px;
      right: 40px;
      width: 40px;
      height: 40px;
      background: var(--gradient-primary);
      border-radius: 50%;
      box-shadow: var(--neon-primary);
    }

    .category-title {
      font-size: 1.8rem;
      font-weight: 800;
      margin-bottom: 20px;
      color: var(--dark);
      position: relative;
      padding-right: 20px;
    }

    .category-title::before {
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
      width: 10px;
      height: 10px;
      background: var(--gradient-primary);
      border-radius: 50%;
    }

    .category-link {
      display: inline-flex;
      align-items: center;
      gap: 15px;
      color: var(--primary);
      font-weight: 700;
      font-size: 1.1rem;
      margin-top: 25px;
      padding: 12px 25px;
      background: rgba(106, 17, 203, 0.1);
      border-radius: 30px;
      transition: var(--transition);
    }

    .category-card:hover .category-link {
      background: var(--gradient-primary);
      color: white;
      transform: translateX(-10px);
      box-shadow: var(--neon-primary);
    }

    /* ============== Courses Section ============== */
    .courses {
      background: white;
      position: relative;
      overflow: hidden;
    }

    .courses::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle at 80% 20%, rgba(106, 17, 203, 0.05) 0%, transparent 50%);
    }

    .courses-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 60px;
      flex-wrap: wrap;
      gap: 30px;
      position: relative;
      z-index: 2;
    }

    .courses-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
      gap: 40px;
      position: relative;
      z-index: 2;
    }

    .course-card {
      background: white;
      border-radius: var(--border-radius-lg);
      overflow: hidden;
      box-shadow: var(--box-shadow-md);
      transition: var(--transition);
      border: 2px solid transparent;
      position: relative;
    }

    .course-card:hover {
      transform: translateY(-20px);
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover);
    }

    .course-card:hover .course-image {
      transform: scale(1.1);
    }

    .course-card:hover .course-badge {
      transform: scale(1.1) rotate(10deg);
    }

    .course-image {
      height: 250px;
      background: linear-gradient(45deg, var(--primary), var(--secondary));
      position: relative;
      overflow: hidden;
      transition: transform 0.8s ease;
    }

    .course-badge {
      position: absolute;
      top: 25px;
      right: 25px;
      background: var(--gradient-accent);
      color: white;
      padding: 8px 20px;
      border-radius: 30px;
      font-size: 0.9rem;
      font-weight: 700;
      box-shadow: var(--neon-accent);
      transition: var(--transition);
      z-index: 2;
    }

    .course-content {
      padding: 35px 30px;
    }

    .course-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 20px;
      border-bottom: 2px solid var(--light-gray);
    }

    .course-meta-item {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--gray);
      font-weight: 600;
      font-size: 0.95rem;
    }

    .course-meta-item svg {
      color: var(--primary);
    }

    .course-title {
      font-size: 1.6rem;
      font-weight: 800;
      margin-bottom: 15px;
      color: var(--dark);
      line-height: 1.4;
    }

    .course-instructor {
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--primary);
      font-weight: 700;
      margin-bottom: 20px;
      padding: 12px 20px;
      background: rgba(106, 17, 203, 0.1);
      border-radius: var(--border-radius-sm);
    }

    .course-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 25px;
      padding-top: 25px;
      border-top: 2px solid var(--light-gray);
    }

    .course-price {
      font-size: 2.5rem;
      font-weight: 900;
      color: var(--primary-dark);
      position: relative;
    }

    .course-price::before {
      content: '$';
      position: absolute;
      top: 0;
      right: -20px;
      font-size: 1.2rem;
      color: var(--gray);
    }

    .course-actions {
      display: flex;
      gap: 15px;
    }

    .btn-favorite, .btn-share {
      width: 45px;
      height: 45px;
      border-radius: 50%;
      background: var(--light-gray);
      border: none;
      color: var(--dark);
      font-size: 1.1rem;
      cursor: pointer;
      transition: var(--transition);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .btn-favorite:hover {
      background: var(--gradient-accent);
      color: white;
      transform: scale(1.1);
    }

    .btn-share:hover {
      background: var(--gradient-primary);
      color: white;
      transform: scale(1.1);
    }

    /* ============== Trainers Section ============== */
    .trainers {
      background: var(--gradient-dark);
      color: white;
      position: relative;
      overflow: hidden;
    }

    .trainers::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 20% 30%, rgba(106, 17, 203, 0.2) 0%, transparent 50%),
        radial-gradient(circle at 80% 70%, rgba(0, 219, 222, 0.2) 0%, transparent 50%);
    }

    .trainers .section-title {
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .trainers .section-subtitle {
      color: rgba(255, 255, 255, 0.8);
    }

    .trainers-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 40px;
      position: relative;
      z-index: 2;
    }

    .trainer-card {
      background: var(--glass-bg);
      backdrop-filter: blur(20px);
      border-radius: var(--border-radius-lg);
      padding: 40px 35px;
      text-align: center;
      transition: var(--transition);
      cursor: pointer;
      border: 1px solid var(--glass-border);
      position: relative;
      overflow: hidden;
    }

    .trainer-card:hover {
      transform: translateY(-20px);
      background: rgba(255, 255, 255, 0.15);
      border-color: var(--secondary);
      box-shadow: var(--box-shadow-hover);
    }

    .trainer-card:hover .trainer-avatar {
      transform: scale(1.1);
    }

    .trainer-avatar {
      width: 150px;
      height: 150px;
      margin: 0 auto 30px;
      position: relative;
      transition: var(--transition);
    }

    .trainer-avatar img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
      border: 4px solid var(--secondary);
      box-shadow: var(--neon-secondary);
    }

    .trainer-avatar::before {
      content: '';
      position: absolute;
      top: -10px;
      right: -10px;
      bottom: -10px;
      left: -10px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      animation: rotate 20s linear infinite;
    }

    .trainer-name {
      font-size: 1.8rem;
      font-weight: 800;
      margin-bottom: 10px;
      background: linear-gradient(45deg, white, var(--secondary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .trainer-specialty {
      color: var(--secondary);
      font-weight: 700;
      font-size: 1.1rem;
      margin-bottom: 20px;
      display: inline-block;
      padding: 8px 20px;
      background: rgba(0, 219, 222, 0.1);
      border-radius: 30px;
    }

    .trainer-social {
      display: flex;
      justify-content: center;
      gap: 15px;
      margin-top: 25px;
    }

    /* ============== Plans Section ============== */
    .plans {
      background: white;
      position: relative;
    }

    .plans::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
      opacity: 0.5;
    }

    .plans-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
      gap: 40px;
      max-width: 1200px;
      margin: 0 auto;
      position: relative;
      z-index: 2;
    }

    .plan-card {
      background: white;
      border-radius: var(--border-radius-lg);
      padding: 50px 40px;
      box-shadow: var(--box-shadow-md);
      transition: var(--transition);
      position: relative;
      border: 2px solid transparent;
      text-align: center;
    }

    .plan-card.featured {
      transform: scale(1.05);
      border-color: var(--primary);
      background: linear-gradient(135deg, white, #f8f9fa);
      box-shadow: var(--box-shadow-hover);
      z-index: 2;
    }

    .plan-card.featured .plan-header {
      background: var(--gradient-primary);
      color: white;
      margin: -50px -40px 40px;
      padding: 50px 40px;
      border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    }

    .plan-card.featured .plan-title,
    .plan-card.featured .price-amount,
    .plan-card.featured .price-period {
      color: white;
    }

    .featured-badge {
      position: absolute;
      top: -20px;
      right: 50%;
      transform: translateX(50%);
      background: var(--gradient-primary);
      color: white;
      padding: 12px 35px;
      border-radius: 30px;
      font-size: 1rem;
      font-weight: 800;
      box-shadow: var(--neon-primary);
      z-index: 3;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .featured-badge svg {
      font-size: 1.2rem;
      animation: pulse 2s ease-in-out infinite;
    }

    .plan-header {
      margin-bottom: 40px;
      padding-bottom: 30px;
      border-bottom: 2px solid var(--light-gray);
    }

    .plan-title {
      font-size: 2rem;
      font-weight: 800;
      margin-bottom: 20px;
      color: var(--dark);
    }

    .plan-price {
      margin-bottom: 10px;
    }

    .price-amount {
      font-size: 4rem;
      font-weight: 900;
      color: var(--primary-dark);
      line-height: 1;
    }

    .price-period {
      color: var(--gray);
      font-size: 1.1rem;
      font-weight: 600;
    }

    .plan-features {
      margin-bottom: 40px;
    }

    .features-list {
      list-style: none;
      text-align: right;
    }

    .features-list li {
      display: flex;
      align-items: center;
      gap: 15px;
      margin-bottom: 20px;
      color: var(--dark);
      font-size: 1.1rem;
      padding: 12px 20px;
      border-radius: var(--border-radius-sm);
      background: rgba(106, 17, 203, 0.05);
      transition: var(--transition);
    }

    .features-list li:hover {
      background: rgba(106, 17, 203, 0.1);
      transform: translateX(-10px);
    }

    .features-list li svg {
      color: var(--success);
      font-size: 1.2rem;
      flex-shrink: 0;
    }

    .features-list li.disabled {
      color: var(--gray);
      opacity: 0.6;
      background: rgba(108, 117, 125, 0.05);
    }

    .features-list li.disabled svg {
      color: var(--gray);
    }

    /* ============== Testimonials ============== */
    .testimonials {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      position: relative;
      overflow: hidden;
    }

    .testimonials::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle at 70% 30%, rgba(106, 17, 203, 0.1) 0%, transparent 50%);
    }

    .testimonials-slider {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
      gap: 40px;
      max-width: 1300px;
      margin: 0 auto;
      position: relative;
      z-index: 2;
    }

    .testimonial-card {
      background: white;
      border-radius: var(--border-radius-lg);
      padding: 50px 40px;
      box-shadow: var(--box-shadow-md);
      transition: var(--transition);
      position: relative;
      overflow: hidden;
    }

    .testimonial-card:hover {
      transform: translateY(-20px);
      box-shadow: var(--box-shadow-hover);
    }

    .testimonial-card:hover .quote-icon {
      transform: scale(1.2) rotate(10deg);
    }

    .quote-icon {
      position: absolute;
      top: 40px;
      right: 40px;
      font-size: 3rem;
      color: var(--primary);
      opacity: 0.1;
      transition: var(--transition);
    }

    .testimonial-content {
      font-size: 1.2rem;
      line-height: 1.9;
      color: var(--dark);
      margin-bottom: 40px;
      font-style: italic;
      position: relative;
      z-index: 2;
    }

    .testimonial-author {
      display: flex;
      align-items: center;
      gap: 20px;
    }

    .author-avatar {
      width: 70px;
      height: 70px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.8rem;
      font-weight: 800;
      flex-shrink: 0;
      box-shadow: var(--neon-primary);
    }

    .author-info {
      text-align: right;
      flex: 1;
    }

    .author-name {
      font-size: 1.4rem;
      font-weight: 800;
      color: var(--dark);
      margin-bottom: 5px;
    }

    .author-role {
      color: var(--primary);
      font-weight: 700;
      font-size: 1rem;
    }

    .rating {
      display: flex;
      gap: 5px;
      color: var(--warning);
      margin-top: 10px;
    }

    .star-icon {
      font-size: 1.2rem;
      filter: drop-shadow(0 0 5px rgba(253, 203, 110, 0.5));
    }

    /* ============== FAQ ============== */
    .faq {
      background: white;
      position: relative;
    }

    .faq::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle at 30% 80%, rgba(106, 17, 203, 0.05) 0%, transparent 50%);
    }

    .faq-list {
      max-width: 900px;
      margin: 0 auto;
      position: relative;
      z-index: 2;
    }

    .faq-item {
      background: white;
      border-radius: var(--border-radius-lg);
      margin-bottom: 20px;
      overflow: hidden;
      border: 2px solid transparent;
      transition: var(--transition);
      box-shadow: var(--box-shadow-sm);
    }

    .faq-item.active {
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover);
    }

    .faq-question {
      padding: 30px 40px;
      display: flex;
      align-items: center;
      gap: 20px;
      cursor: pointer;
      transition: var(--transition);
      background: white;
    }

    .faq-item.active .faq-question {
      background: var(--gradient-primary);
      color: white;
    }

    .faq-icon {
      font-size: 1.5rem;
      flex-shrink: 0;
      color: var(--primary);
    }

    .faq-item.active .faq-icon {
      color: white;
    }

    .question-text {
      font-size: 1.3rem;
      font-weight: 700;
      flex: 1;
      margin: 0;
    }

    .arrow-icon {
      transition: var(--transition);
      color: var(--gray);
    }

    .faq-item.active .arrow-icon {
      transform: rotate(90deg);
      color: white;
    }

    .faq-answer {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.6s ease;
    }

    .faq-item.active .faq-answer {
      max-height: 500px;
    }

    .faq-answer-content {
      padding: 0 40px 40px;
      color: var(--gray);
      line-height: 1.9;
      font-size: 1.1rem;
    }

    /* ============== CTA Section ============== */
    .cta {
      background: var(--gradient-dark);
      color: white;
      text-align: center;
      padding: 150px 0;
      position: relative;
      overflow: hidden;
    }

    .cta::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 20% 50%, rgba(106, 17, 203, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 80% 50%, rgba(0, 219, 222, 0.3) 0%, transparent 50%);
      animation: gradient 10s ease infinite;
      background-size: 400% 400%;
    }

    .cta-content {
      max-width: 900px;
      margin: 0 auto;
      position: relative;
      z-index: 2;
    }

    .cta-title {
      font-size: 4.5rem;
      font-weight: 900;
      margin-bottom: 30px;
      background: linear-gradient(45deg, white, var(--secondary), var(--primary-light));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.1;
    }

    .cta-subtitle {
      font-size: 1.5rem;
      opacity: 0.95;
      margin-bottom: 50px;
      max-width: 700px;
      margin-left: auto;
      margin-right: auto;
      line-height: 1.8;
    }

    .cta-buttons {
      display: flex;
      gap: 25px;
      justify-content: center;
      flex-wrap: wrap;
    }

    /* ============== Contact Section ============== */
    .contact {
      background: white;
      position: relative;
    }

    .contact::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%236a11cb' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E");
      opacity: 0.3;
    }

    .contact-wrapper {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      align-items: center;
      position: relative;
      z-index: 2;
    }

    .contact-info {
      padding-left: 30px;
    }

    .contact-item {
      display: flex;
      align-items: center;
      gap: 25px;
      margin-bottom: 35px;
      color: var(--dark);
      padding: 25px 30px;
      background: white;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition);
    }

    .contact-item:hover {
      transform: translateX(-15px);
      box-shadow: var(--box-shadow-hover);
      border-right: 5px solid var(--primary);
    }

    .contact-icon {
      width: 70px;
      height: 70px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.8rem;
      flex-shrink: 0;
      box-shadow: var(--neon-primary);
    }

    .contact-item h4 {
      font-size: 1.4rem;
      font-weight: 800;
      margin-bottom: 8px;
      color: var(--dark);
    }

    .contact-item p {
      color: var(--gray);
      font-size: 1.1rem;
    }

    .social-links {
      display: flex;
      gap: 15px;
      margin-top: 40px;
    }

    .contact-form {
      background: white;
      padding: 50px 40px;
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-md);
      position: relative;
      overflow: hidden;
    }

    .contact-form::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: var(--gradient-primary);
    }

    .contact-form h3 {
      font-size: 2.2rem;
      font-weight: 800;
      margin-bottom: 40px;
      color: var(--dark);
      text-align: center;
      background: linear-gradient(45deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .form-group {
      margin-bottom: 30px;
    }

    .form-label {
      display: block;
      margin-bottom: 12px;
      font-weight: 700;
      color: var(--dark);
      font-size: 1.1rem;
    }

    .form-input {
      width: 100%;
      padding: 18px 25px;
      border: 2px solid var(--light-gray);
      border-radius: var(--border-radius-md);
      font-size: 1.1rem;
      transition: var(--transition);
      background: white;
    }

    .form-input:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 4px rgba(106, 17, 203, 0.1);
      transform: translateY(-2px);
    }

    .form-textarea {
      min-height: 180px;
      resize: vertical;
    }

    /* ============== Footer ============== */
    .footer {
      background: var(--gradient-dark);
      color: white;
      padding: 100px 0 40px;
      position: relative;
      overflow: hidden;
    }

    .footer::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 20% 30%, rgba(106, 17, 203, 0.2) 0%, transparent 50%),
        radial-gradient(circle at 80% 70%, rgba(0, 219, 222, 0.2) 0%, transparent 50%);
    }

    .footer-content {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 50px;
      margin-bottom: 70px;
      position: relative;
      z-index: 2;
    }

    .footer-column h3 {
      font-size: 1.6rem;
      font-weight: 800;
      margin-bottom: 35px;
      color: white;
      position: relative;
      padding-bottom: 15px;
    }

    .footer-column h3::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 50px;
      height: 3px;
      background: var(--gradient-primary);
      border-radius: 2px;
    }

    .footer-links {
      list-style: none;
    }

    .footer-links li {
      margin-bottom: 18px;
    }

    .footer-links a {
      color: rgba(255, 255, 255, 0.8);
      text-decoration: none;
      transition: var(--transition);
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 1.1rem;
      padding: 10px 0;
      border-radius: var(--border-radius-sm);
    }

    .footer-links a:hover {
      color: var(--secondary);
      padding-right: 15px;
      background: rgba(255, 255, 255, 0.05);
    }

    .footer-links a svg {
      color: var(--primary);
      font-size: 0.9rem;
    }

    .footer-links a:hover svg {
      color: var(--secondary);
      transform: translateX(-5px);
    }

    .newsletter {
      margin-top: 30px;
    }

    .newsletter-input {
      width: 100%;
      padding: 15px 20px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: var(--border-radius-md);
      color: white;
      font-size: 1rem;
      margin-bottom: 15px;
    }

    .newsletter-input::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }

    .copyright {
      text-align: center;
      padding-top: 40px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.7);
      font-size: 1rem;
      position: relative;
      z-index: 2;
    }

    /* ============== Responsive ============== */
    @media (max-width: 1200px) {
      .container { max-width: 100%; }
      .hero-title { font-size: 3.8rem; }
      .section-title { font-size: 3rem; }
      .cta-title { font-size: 3.5rem; }
    }

    @media (max-width: 992px) {
      .hero-title { font-size: 3.2rem; }
      .nav-links { display: none; }
      .mobile-menu-btn { display: block; }
      .contact-wrapper { grid-template-columns: 1fr; gap: 50px; }
      .footer-content { grid-template-columns: repeat(2, 1fr); }
      .hero-stats { grid-template-columns: repeat(2, 1fr); }
      .features-grid { grid-template-columns: 1fr; }
    }

    @media (max-width: 768px) {
      .hero { min-height: auto; padding: 150px 0 80px; }
      .hero-title { font-size: 2.5rem; }
      .hero-subtitle { font-size: 1.2rem; }
      .hero-buttons { flex-direction: column; width: 100%; }
      .hero-buttons .btn { width: 100%; }
      .section-title { font-size: 2.5rem; }
      .section-badge { font-size: 0.8rem; }
      .cta-title { font-size: 2.8rem; }
      .cta { padding: 100px 0; }
      .courses-grid { grid-template-columns: 1fr; }
      .trainers-grid { grid-template-columns: 1fr; }
      .plans-grid { grid-template-columns: 1fr; }
      .plan-card.featured { transform: none; }
      .testimonials-slider { grid-template-columns: 1fr; }
      .contact-form { padding: 40px 30px; }
      .floating-element { display: none; }
    }

    @media (max-width: 576px) {
      .container { padding: 0 20px; }
      .hero-title { font-size: 2.2rem; }
      .hero-subtitle { font-size: 1.1rem; }
      .hero-stats { grid-template-columns: 1fr; gap: 20px; }
      .stat-number { font-size: 2.5rem; }
      .section-title { font-size: 2rem; }
      .section-subtitle { font-size: 1.1rem; }
      .cta-title { font-size: 2.2rem; }
      .cta-subtitle { font-size: 1.2rem; }
      .footer-content { grid-template-columns: 1fr; }
      .mobile-menu { width: 100%; }
      .btn { padding: 14px 28px; font-size: 1rem; }
    }

    /* ============== Utilities ============== */
    .text-center { text-align: center; }
    .mt-20 { margin-top: 20px; }
    .mt-30 { margin-top: 30px; }
    .mt-40 { margin-top: 40px; }
    .mb-20 { margin-bottom: 20px; }
    .mb-30 { margin-bottom: 30px; }
    .mb-40 { margin-bottom: 40px; }
    .p-20 { padding: 20px; }
    .p-30 { padding: 30px; }
    .p-40 { padding: 40px; }
    .glass { background: var(--glass-bg); backdrop-filter: blur(10px); border: 1px solid var(--glass-border); }
    .shadow-lg { box-shadow: var(--box-shadow-lg); }
    .shadow-md { box-shadow: var(--box-shadow-md); }
    .shadow-sm { box-shadow: var(--box-shadow-sm); }
    .rounded-lg { border-radius: var(--border-radius-lg); }
    .rounded-md { border-radius: var(--border-radius-md); }
    .rounded-sm { border-radius: var(--border-radius-sm); }
    .gradient-text { background: var(--gradient-primary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
    .neon-shadow { box-shadow: var(--neon-primary); }
  `;

  if (loading) return (
    <div className="loading-container">
      <style>{styles}</style>
      <div className="spinner"></div>
      <h2>جارٍ تحميل الأكاديمية...</h2>
    </div>
  );
  
  if (!academy) return (
    <div className="error-container">
      <style>{styles}</style>
      <h2>الأكاديمية غير موجودة</h2>
      <button 
        onClick={() => navigate("/")} 
        className="btn btn-primary mt-30"
        style={{background: 'white', color: '#ff6b6b', position: 'relative', zIndex: 2}}
      >
        <FaArrowRight /> العودة للرئيسية
      </button>
    </div>
  );

  return (
    <div className="academy-landing">
      <style>{styles}</style>

      {/* ================= HEADER ================= */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <a href="#" className="logo" onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}>
              <div className="logo-icon">
                <FaGraduationCap />
              </div>
              <span className="logo-text">{academy.name}</span>
            </a>

            <nav className="nav">
              <ul className="nav-links">
                <li><a href="#features" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>المميزات</a></li>
                <li><a href="#categories" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('categories'); }}>التخصصات</a></li>
                <li><a href="#courses" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('courses'); }}>الكورسات</a></li>
                <li><a href="#trainers" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('trainers'); }}>المدربون</a></li>
                <li><a href="#plans" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('plans'); }}>الخطط</a></li>
                <li><a href="#contact" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>تواصل</a></li>
              </ul>
            </nav>

            <div className="header-actions">
              <button className="search-btn">
                <FaSearch />
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => navigate(`/academy/${academyId}/student/register`)}
              >
                <FaUserGraduate /> ابدأ الآن
              </button>
              <button 
                className="mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ================= MOBILE MENU ================= */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-links">
          <li><a href="#hero" className="mobile-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}><FaRocket /> الرئيسية</a></li>
          <li><a href="#features" className="mobile-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}><FaMagic  /> المميزات</a></li>
          <li><a href="#categories" className="mobile-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('categories'); }}><FaBook /> التخصصات</a></li>
          <li><a href="#courses" className="mobile-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('courses'); }}><FaPlay /> الكورسات</a></li>
          <li><a href="#trainers" className="mobile-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('trainers'); }}><FaChalkboardTeacher /> المدربون</a></li>
          <li><a href="#plans" className="mobile-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('plans'); }}><FaGem /> الخطط</a></li>
          <li><a href="#contact" className="mobile-nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}><FaPhone /> تواصل</a></li>
        </ul>
        
        <div className="mobile-menu-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/academy/${academyId}/student/register`)}
          >
            <FaUserGraduate /> انضم إلينا
          </button>
          <button 
            className="btn btn-outline"
            onClick={() => setMobileMenuOpen(false)}
          >
            <FaTimes /> إغلاق
          </button>
        </div>
      </div>

      {/* ================= HERO SECTION ================= */}
      <section id="hero" className="hero">
        <div className="floating-elements">
          <div className="floating-element"><FaGraduationCap /></div>
          <div className="floating-element"><FaStar /></div>
          <div className="floating-element"><FaRocket /></div>
          <div className="floating-element"><FaTrophy /></div>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">{academy.name}</h1>
            <p className="hero-subtitle">{academy.description}</p>
            
            <div className="hero-buttons">
              <button
                onClick={() => navigate(`/academy/${academyId}/student/register`)}
                className="btn btn-primary"
              >
                <FaRocket /> ابدأ التعلم الآن
              </button>
              <button
                onClick={() => scrollToSection('courses')}
                className="btn btn-glass"
              >
                <FaPlay /> شاهد الكورسات
              </button>
            </div>
            
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-icon"><FaChalkboardTeacher /></div>
                <div className="stat-number">{trainers.length}+</div>
                <div className="stat-label">مدرب محترف</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon"><FaBook /></div>
                <div className="stat-number">{latestCourses.length}+</div>
                <div className="stat-label">كورس متاح</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon"><FaUsers /></div>
                <div className="stat-number">1000+</div>
                <div className="stat-label">طالب مسجل</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon"><FaStar /></div>
                <div className="stat-number">4.9</div>
                <div className="stat-label">تقييم الأكاديمية</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES SECTION ================= */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <span className="section-badge"><FaMagic  /> لماذا تختار {academy.name}؟</span>
            <h2 className="section-title">مميزات فريدة تجعلنا الأفضل</h2>
            <p className="section-subtitle">نقدم لك تجربة تعليمية استثنائية مع مزايا لا تجدها في مكان آخر</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <FaChalkboardTeacher />
              </div>
              <h3 className="feature-title">مدربون محترفون</h3>
              <p className="feature-description">
                تعلم من أفضل الخبراء في المجال مع سنوات من الخبرة العملية والتدريب المهني
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <FaCertificate />
              </div>
              <h3 className="feature-title">شهادات معتمدة</h3>
              <p className="feature-description">
                احصل على شهادات معترف بها دولياً تفتح لك أبواب الفرص الوظيفية
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <FaUsers />
              </div>
              <h3 className="feature-title">مجتمع تفاعلي</h3>
              <p className="feature-description">
                انضم إلى مجتمع نشط من المتعلمين، وانخرط في النقاشات والدروس الحية
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <FaClock />
              </div>
              <h3 className="feature-title">تعلم مرن</h3>
              <p className="feature-description">
                تعلم في أي وقت ومن أي مكان، مع وصول مدى الحياة للمحتوى والدروس
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <FaHeadset />
              </div>
              <h3 className="feature-title">دعم فني 24/7</h3>
              <p className="feature-description">
                فريق دعم فني متاح على مدار الساعة لمساعدتك في أي استفسار أو مشكلة
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <FaChartLine />
              </div>
              <h3 className="feature-title">تقدم مضمون</h3>
              <p className="feature-description">
                خطط تعليمية مدروسة تضمن تقدمك المستمر ووصولك لأهدافك التعليمية
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CATEGORIES SECTION ================= */}
      <section id="categories" className="categories">
        <div className="container">
          <div className="section-header">
            <span className="section-badge"><FaPalette /> التخصصات الرائدة</span>
            <h2 className="section-title">اكتشف عالم المعرفة</h2>
            <p className="section-subtitle">اختر مجال تخصصك وابدأ رحلة التعلم نحو النجاح المهني</p>
          </div>
          
          {mainCategories.length === 0 ? (
            <div className="empty-state text-center">
              <FaBook size={80} color="var(--primary)" style={{marginBottom: '20px'}} />
              <p style={{fontSize: '1.2rem', color: 'var(--gray)'}}>لا توجد فئات متاحة حالياً</p>
            </div>
          ) : (
            <div className="categories-grid">
              {mainCategories.map((cat, index) => {
                const icons = [FaGlobe, FaLaptopCode, FaPaintBrush, FaBullhorn, FaBriefcase];
                const Icon = icons[index % icons.length];
                return (
                  <div
                    key={cat.id}
                    onClick={() => navigate(`/academies/${academyId}/categories/${cat.id}`)}
                    className="category-card"
                  >
                    <div className="category-image">
                      <Icon style={{
                        position: 'absolute',
                        top: '50%',
                        right: '50%',
                        transform: 'translate(50%, -50%)',
                        fontSize: '5rem',
                        color: 'white',
                        opacity: '0.3'
                      }} />
                    </div>
                    <div className="category-content">
                      <h3 className="category-title">{cat.name}</h3>
                      <p className="feature-description">
                        {cat.description || "استكشف أفضل الدورات في هذا التخصص"}
                      </p>
                      <span className="category-link">
                        تصفح الكورسات <FaChevronRight />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ================= COURSES SECTION ================= */}
      <section id="courses" className="courses">
        <div className="container">
          <div className="courses-header">
            <div>
              <span className="section-badge"><FaFire /> الأكثر طلباً</span>
              <h2 className="section-title">أحدث الكورسات</h2>
              <p className="section-subtitle">اكتشف أحدث الدورات المضافة وأكثرها طلباً</p>
            </div>
            <button 
              className="btn btn-outline"
              onClick={() => navigate(`/academies/${academyId}/courses`)}
            >
              <FaEye /> عرض جميع الكورسات
            </button>
          </div>
          
          {latestCourses.length === 0 ? (
            <div className="empty-state text-center">
              <FaBook size={80} color="var(--primary)" style={{marginBottom: '20px'}} />
              <p style={{fontSize: '1.2rem', color: 'var(--gray)'}}>لا توجد كورسات حالياً</p>
            </div>
          ) : (
            <div className="courses-grid">
              {latestCourses.slice(0, 3).map((course) => (
                <div key={course.id} className="course-card">
                  <div className="course-image">
                    <div className="course-badge">
                      <FaFire style={{marginLeft: '5px'}} /> الأكثر طلباً
                    </div>
                  </div>
                  <div className="course-content">
                    <div className="course-meta">
                      <span className="course-meta-item">
                        <FaClock /> 10 ساعات
                      </span>
                      <span className="course-meta-item">
                        <FaLanguage /> متاح بـ 3 لغات
                      </span>
                    </div>
                    <h3 className="course-title">{course.title}</h3>
                    <p className="course-instructor">
                      <FaChalkboardTeacher /> {course.trainer_name || "مدرب محترف"}
                    </p>
                    <p className="feature-description">
                      {course.description || "انضم إلى هذه الدورة لتعلم المهارات الجديدة"}
                    </p>
                    <div className="course-footer">
                      <div>
                        <div className="course-price">{course.price || "49.99"}</div>
                        <small style={{color: 'var(--gray)'}}>شامل الضريبة</small>
                      </div>
                      <div className="course-actions">
                        <button className="btn-favorite">
                          <FaRegHeart />
                        </button>
                        <button className="btn-share">
                          <FaShare />
                        </button>
                        <button className="btn btn-primary">
                          سجل الآن
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ================= TRAINERS SECTION ================= */}
      <section id="trainers" className="trainers">
        <div className="container">
          <div className="section-header">
            <span className="section-badge glass"><FaTrophy /> الخبراء</span>
            <h2 className="section-title">مدربونا المحترفون</h2>
            <p className="section-subtitle">تعلم من أفضل الخبراء والمتخصصين في المجال</p>
          </div>
          
          {trainers.length === 0 ? (
            <div className="empty-state text-center">
              <FaChalkboardTeacher size={80} color="var(--secondary)" style={{marginBottom: '20px'}} />
              <p style={{fontSize: '1.2rem', color: 'rgba(255,255,255,0.8)'}}>لا يوجد مدربون بعد</p>
            </div>
          ) : (
            <div className="trainers-grid">
              {trainers.slice(0, 4).map((trainer) => (
                <div
                  key={trainer.id}
                  onClick={() => navigate(`/trainers/${trainer.id}`)}
                  className="trainer-card"
                >
                  <div className="trainer-avatar">
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(45deg, var(--primary), var(--secondary))',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '3rem',
                      fontWeight: '800',
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)'
                    }}>
                      {trainer.name.charAt(0)}
                    </div>
                  </div>
                  <h3 className="trainer-name">{trainer.name}</h3>
                  <p className="trainer-specialty">خبير في {trainer.specialty || "التدريب والتعليم"}</p>
                  <p className="feature-description" style={{color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem'}}>
                    {trainer.bio || "خبير في مجال التعليم والتطوير"}
                  </p>
                  <div className="trainer-social">
                    <button className="btn-social"><FaFacebookF /></button>
                    <button className="btn-social"><FaTwitter /></button>
                    <button className="btn-social"><FaLinkedinIn /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ================= PLANS SECTION ================= */}
   {/* ================= PLANS SECTION ================= */}
{plans.length > 0 && (
  <section id="plans" className="plans">
    <div className="container">
      <div className="section-header">
        <span className="section-badge"><FaGem /> الخطط المميزة</span>
        <h2 className="section-title">خطط الاشتراك</h2>
        <p className="section-subtitle">اختر الخطة المناسبة لاحتياجاتك التعليمية</p>
      </div>
      
      <div className="plans-grid">
        {plans.map((plan, index) => (
          <div 
            key={plan.id} 
            className={`plan-card ${index === 1 ? 'featured' : ''}`} // أو plan.is_featured لو موجود
          >
            {index === 1 && (
              <div className="featured-badge">
                <FaCrown /> الأكثر شعبية
              </div>
            )}
            
            <div className="plan-header">
              <h3 className="plan-title">{plan.title}</h3>
              <div className="plan-price">
                <span className="price-amount">${plan.price}</span>
                <span className="price-period">/{plan.duration} يوم</span>
              </div>
              {index === 1 && (
                <p style={{color: 'rgba(255,255,255,0.9)', marginTop: '10px'}}>
                  {plan.discount || 'وفر 30% عند الاشتراك السنوي'}
                </p>
              )}
            </div>
            
            <div className="plan-features">
              <p className="feature-description" style={{
                color: index === 1 ? 'rgba(255,255,255,0.9)' : 'var(--gray)',
                fontSize: '1.1rem',
                marginBottom: '30px'
              }}>
                {plan.description}
              </p>
              
              {/* ✅ عرض الميزات الحقيقية من الباك إند */}
              {plan.features && plan.features.length > 0 ? (
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={feature.id || idx}>
                      <FaCheck /> {feature.feature}
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="features-list">
                  <li><FaCheck /> وصول كامل للكورسات</li>
                  <li><FaCheck /> شهادات معتمدة</li>
                  <li><FaCheck /> دعم فني 24/7</li>
                  <li><FaCheck /> تحديثات مستمرة</li>
                  <li className={index !== 1 ? 'disabled' : ''}>
                    <FaCheck /> جلسات إرشاد فردية
                  </li>
                  <li className={index !== 1 ? 'disabled' : ''}>
                    <FaCheck /> مكتبة الكتب الإلكترونية
                  </li>
                </ul>
              )}
            </div>
            
            <button className="btn btn-primary btn-subscribe">
              <FaRocket /> اشترك الآن
            </button>
          </div>
        ))}
      </div>
      
      <div className="text-center mt-40">
        <p style={{color: 'var(--gray)', fontSize: '1.1rem', maxWidth: '700px', margin: '0 auto'}}>
          <FaShieldAlt style={{marginLeft: '10px', color: 'var(--success)'}} />
          جميع الخطط تشمل ضمان استرداد الأموال خلال 30 يوم
        </p>
      </div>
    </div>
  </section>
)}

      {/* ================= TESTIMONIALS SECTION ================= */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="testimonials">
          <div className="container">
            <div className="section-header">
              <span className="section-badge"><FaQuoteRight /> آراء الخريجين</span>
              <h2 className="section-title">ماذا يقول طلابنا</h2>
              <p className="section-subtitle">انضم إلى آلاف الطلاب الناجحين الذين غيروا حياتهم معنا</p>
            </div>
            
            <div className="testimonials-slider">
              {testimonials.map((t, index) => (
                <div key={t.id} className="testimonial-card">
                  <FaQuoteRight className="quote-icon" />
                  <p className="testimonial-content">"{t.content}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {t.name.charAt(0)}
                    </div>
                    <div className="author-info">
                      <h4 className="author-name">{t.name}</h4>
                      <p className="author-role">خريج {new Date().getFullYear() - index}</p>
                      <div className="rating">
                        {[...Array(5)].map((_, i) => (
                          <FaStar key={i} className="star-icon" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= FAQ SECTION ================= */}
      {faqs.length > 0 && (
        <section id="faq" className="faq">
          <div className="container">
            <div className="section-header">
              <span className="section-badge"><FaQuestionCircle /> الأسئلة الشائعة</span>
              <h2 className="section-title">إجابات على أسئلتك</h2>
              <p className="section-subtitle">كل ما تريد معرفته عن {academy.name} في مكان واحد</p>
            </div>
            
            <div className="faq-list">
              {faqs.map((f, index) => (
                <div 
                  key={f.id} 
                  className={`faq-item ${activeFaq === index ? 'active' : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <div className="faq-question">
                    <FaQuestionCircle className="faq-icon" />
                    <h3 className="question-text">{f.question}</h3>
                    <FaChevronRight className="arrow-icon" />
                  </div>
                  <div className="faq-answer">
                    <div className="faq-answer-content">
                      <p>{f.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-40">
              <p style={{color: 'var(--gray)', fontSize: '1.1rem'}}>
                لا تجد إجابة لسؤالك؟ <button 
                  className="btn-text"
                  onClick={() => scrollToSection('contact')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    fontWeight: '700',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    textDecoration: 'underline'
                  }}
                >
                  اتصل بنا
                </button>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ================= CTA SECTION ================= */}
      <section id="cta" className="cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">جاهز لبدء رحلة النجاح؟</h2>
            <p className="cta-subtitle">
              انضم إلى أكثر من 10,000 طالب حول العالم، وابدأ رحلتك نحو التحول المهني مع {academy.name}
            </p>
            <div className="cta-buttons">
              <button
                onClick={() => navigate(`/academy/${academyId}/student/register`)}
                className="btn btn-primary"
                style={{background: 'white', color: 'var(--primary)'}}
              >
                <FaUserGraduate /> ابدأ الآن مجاناً
              </button>
              <button
                onClick={() => scrollToSection('plans')}
                className="btn btn-glass"
              >
                <FaGem /> عرض الخطط
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CONTACT SECTION ================= */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <span className="section-badge"><FaHeadset /> الدعم والمساعدة</span>
            <h2 className="section-title">تواصل معنا</h2>
            <p className="section-subtitle">نحن هنا للإجابة على جميع استفساراتك</p>
          </div>
          
          <div className="contact-wrapper">
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">
                  <FaPhone />
                </div>
                <div>
                  <h4>الدعم الفني</h4>
                  <p>+966 50 123 4567</p>
                  <small style={{color: 'var(--gray)'}}>متاح 24/7</small>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">
                  <FaEnvelope />
                </div>
                <div>
                  <h4>البريد الإلكتروني</h4>
                  <p>support@{academy.name.toLowerCase().replace(/\s/g, '')}.com</p>
                  <small style={{color: 'var(--gray)'}}>الرد خلال 24 ساعة</small>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <h4>المكتب الرئيسي</h4>
                  <p>الرياض، المملكة العربية السعودية</p>
                  <small style={{color: 'var(--gray)'}}>مفتوح: الأحد - الخميس 8ص - 5م</small>
                </div>
              </div>
              
              <div className="social-links">
                <button className="btn-social">
                  <FaFacebookF />
                </button>
                <button className="btn-social">
                  <FaTwitter />
                </button>
                <button className="btn-social">
                  <FaLinkedinIn />
                </button>
                <button className="btn-social">
                  <FaInstagram />
                </button>
                <button className="btn-social">
                  <FaYoutube />
                </button>
              </div>
            </div>
            
            <div className="contact-form">
              <h3>أرسل رسالتك</h3>
              <form>
                <div className="form-group">
                  <label className="form-label">الاسم الكامل</label>
                  <input type="text" className="form-input" placeholder="أدخل اسمك الكامل" />
                </div>
                
                <div className="form-group">
                  <label className="form-label">البريد الإلكتروني</label>
                  <input type="email" className="form-input" placeholder="example@email.com" />
                </div>
                
                <div className="form-group">
                  <label className="form-label">رقم الهاتف</label>
                  <input type="tel" className="form-input" placeholder="+966 50 XXX XXXX" />
                </div>
                
                <div className="form-group">
                  <label className="form-label">الموضوع</label>
                  <select className="form-input">
                    <option>استفسار عام</option>
                    <option>دعم فني</option>
                    <option>مشكلة في الدفع</option>
                    <option>مقترح</option>
                    <option>شكوى</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">الرسالة</label>
                  <textarea 
                    className="form-input form-textarea" 
                    placeholder="اكتب رسالتك هنا..."
                    rows="5"
                  ></textarea>
                </div>
                
                <button type="submit" className="btn btn-primary" style={{width: '100%'}}>
                  <FaPaperPlane /> إرسال الرسالة
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <h3>{academy.name}</h3>
              <p style={{color: 'rgba(255,255,255,0.9)', marginBottom: '25px', lineHeight: '1.8'}}>
                {academy.description.substring(0, 120)}...
              </p>
              <div className="social-links">
                <button className="btn-social">
                  <FaFacebookF />
                </button>
                <button className="btn-social">
                  <FaTwitter />
                </button>
                <button className="btn-social">
                  <FaLinkedinIn />
                </button>
                <button className="btn-social">
                  <FaInstagram />
                </button>
              </div>
            </div>
            
            <div className="footer-column">
              <h3>روابط سريعة</h3>
              <ul className="footer-links">
                <li><a href="#hero" onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}><FaChevronRight /> الرئيسية</a></li>
                <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}><FaMagic  /> المميزات</a></li>
                <li><a href="#courses" onClick={(e) => { e.preventDefault(); scrollToSection('courses'); }}><FaPlay /> الكورسات</a></li>
                <li><a href="#trainers" onClick={(e) => { e.preventDefault(); scrollToSection('trainers'); }}><FaChalkboardTeacher /> المدربون</a></li>
                <li><a href="#plans" onClick={(e) => { e.preventDefault(); scrollToSection('plans'); }}><FaGem /> الخطط</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h3>التخصصات</h3>
              <ul className="footer-links">
                <li><a href="#"><FaChevronRight /> تطوير الويب</a></li>
                <li><a href="#"><FaChevronRight /> تطوير التطبيقات</a></li>
                <li><a href="#"><FaChevronRight /> الذكاء الاصطناعي</a></li>
                <li><a href="#"><FaChevronRight /> علم البيانات</a></li>
                <li><a href="#"><FaChevronRight /> التسويق الرقمي</a></li>
                <li><a href="#"><FaChevronRight /> التصميم الجرافيكي</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h3>القوانين والسياسات</h3>
              <ul className="footer-links">
                <li><a href="#"><FaChevronRight /> شروط الاستخدام</a></li>
                <li><a href="#"><FaChevronRight /> سياسة الخصوصية</a></li>
                <li><a href="#"><FaChevronRight /> سياسة الاسترجاع</a></li>
                <li><a href="#"><FaChevronRight /> الأسئلة الشائعة</a></li>
                <li><a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}><FaChevronRight /> اتصل بنا</a></li>
              </ul>
              
              <div className="newsletter mt-30">
                <h4 style={{marginBottom: '15px', fontSize: '1.1rem'}}>النشرة الإخبارية</h4>
                <input 
                  type="email" 
                  className="newsletter-input" 
                  placeholder="بريدك الإلكتروني"
                />
                <button className="btn btn-secondary" style={{width: '100%'}}>
                  <FaPaperPlane /> اشترك الآن
                </button>
              </div>
            </div>
          </div>
          
          <div className="copyright">
            <p>
              © {new Date().getFullYear()} {academy.name}. جميع الحقوق محفوظة.
              <span style={{marginRight: '20px', marginLeft: '20px'}}>|</span>
              صمم بحب ❤️ لتطوير المجتمع التعليمي
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}